<template>
  <div :class="[isIs24 ? $style.container : $style.hgContainer, { ['layout-container']: isIs24 }]">
    <img
      :src="`${baseUrl}images/localLanding/${routeLocation}/preload-hero-image.webp`"
      :alt="
        $t('localLanding.hero.imageAlt', {
          location: routeLocation,
        })
      "
      :class="[$style.image, { ['col-sm-8']: isIs24 }]"
    />
    <div :class="[isIs24 ? 'col-sm-8 col-md-6' : 'layout-container', $style.infoBox]">
      <h1 :class="$style.title">
        <slot name="heading">
          {{
            $t(`localLanding.hero.title.${propertyType}`, {
              location: routeLocationName,
            })
          }}
        </slot>
      </h1>
      <p :class="$style.description">
        <slot name="description">
          {{
            $t('localLanding.hero.description', {
              location: routeLocationName,
            })
          }}
        </slot>
      </p>
      <div :class="$style.contactCard">
        <h4 :class="$style.contactCardTitle">
          <slot name="headline">
            {{ $t('localLanding.hero.contact.description') }}
          </slot>
        </h4>
        <div :class="$style.contactInfoWrapper">
          <div :class="$style.contactInfo">
            <img
              src="../../../assets/images/local-landing-hero/consultant-profile.webp"
              :alt="$t('localLanding.hero.contact.imageAlt')"
              loading="lazy"
            />
            <div>
              <p :class="$style.contactName">Arian Hajdari</p>
              <p :class="$style.contactDescription">
                {{ $t('localLanding.hero.contact.title') }}
              </p>
            </div>
          </div>
          <HgButton :size="ButtonSize.Large" :class="$style.ctaButton" @click="handleCtaClick"
            >{{ $t('localLanding.hero.contact.cta') }}
          </HgButton>
        </div>
      </div>
    </div>
  </div>
  <InformedContactModal
    ref="contactModalRef"
    :from="CallMeBackModalFrom.TopBanner"
    :modalType="ContactModalType.CALL_ME_BACK"
  />
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { InformedContactModal } from '@/components/common';
import { getLocationInfos } from '@/helpers/GeoLocation/GeoLocationHelper';
import { isPlatformIs24 } from '@/helpers/Platform/Platform';
import { getStaticAssetsBaseUrl } from '@/helpers/Url/Url';
import { useLocalLandingStore } from '@/stores/localLanding/localLanding';
import { trackingGenericClickEvent } from '@/tracking/tracking';
import {
  LOCAL_LANDING_PAGE_ACTION,
  LOCAL_LANDING_PAGE_CATEGORY,
  LOCAL_LANDING_PAGE_EVENT,
} from '@/tracking/types';
import { ContactModalFrom, ContactModalType } from '@/types';
import { ButtonSize, HgButton, HgSupportedLanguage } from '@smg-real-estate/domus';

const localLandingStore = useLocalLandingStore();
const { locale } = useI18n();
const { propertyType, routeGeoLocation, routeLocation } = storeToRefs(localLandingStore);

// Props
const { showCallbackModal = false } = defineProps<{
  showCallbackModal?: boolean;
}>();

// Reactive data
const contactModalRef = ref<InstanceType<typeof InformedContactModal> | null>(null);

// Constants
const baseUrl = getStaticAssetsBaseUrl();
const isIs24 = isPlatformIs24();
const CallMeBackModalFrom = ContactModalFrom;

const routeLocationName = computed((): string => {
  return getLocationInfos(routeGeoLocation.value, locale.value as HgSupportedLanguage).name;
});

// Methods
const handleCtaClick = () => {
  trackingGenericClickEvent({
    event: LOCAL_LANDING_PAGE_EVENT.BUTTON_CLICK,
    category: LOCAL_LANDING_PAGE_CATEGORY.BUTTON_CLICK,
    action: LOCAL_LANDING_PAGE_ACTION.REQUEST_CALLBACK,
    label: 'top_banner_cta',
  });
  contactModalRef.value?.open();
};

onMounted(() => {
  // Ensure smooth modal transition
  nextTick(() => {
    contactModalRef.value?.open();
  });
});
</script>

<style module lang="scss">
@use '@/assets/variables' as *;

$bg-img-height-s: 369px;
$bg-img-height-m: 443px;
$bg-img-height-l: 485px;
$avatar-size: 60px;
$bg-img-height-hg-s: 281px;
$bg-img-height-hg-l: 441px;

.container[class*='layout-container'] {
  position: relative;
  width: 100%;
  padding: 0;

  @include grid-media-query(sm) {
    padding: $space-50 $space-100;
  }

  @include grid-media-query(md) {
    margin-bottom: $space-150;
  }

  .image[class*='col-'] {
    display: block;
    width: 100%;
    height: $bg-img-height-s;
    padding: 0;
    object-fit: cover;

    @include grid-media-query(sm) {
      height: $bg-img-height-m;
      margin-left: auto;
      border-radius: $border-radius-l;
    }

    @include grid-media-query(md) {
      height: $bg-img-height-l;
    }
  }

  .infoBox[class*='col-'] {
    position: relative;
    box-sizing: border-box;
    width: auto;
    padding: $space-100;
    margin: -$space-300 $space-100 0 $space-100;
    background: $color-white;
    border-radius: $border-radius-l;
    box-shadow: $elevation-medium;

    @include grid-media-query(sm) {
      position: absolute;
      top: 50%;
      padding: $space-250 $space-200;
      margin: 0;
      transform: translate(0, -50%);
    }
  }
}

.hgContainer {
  width: 100%;
  height: 600px;

  @include grid-media-query(sm) {
    height: 450px;
  }

  &::before {
    position: absolute;
    width: 100%;
    height: $bg-img-height-hg-l;
    margin-left: 0;
    clip-path: polygon(0% 0%, 0% -120%, 55% 100%, 0 100%);
    content: '';
    background-color: #8da6d6;

    @include grid-media-query(sm) {
      height: $bg-img-height-hg-s;
      clip-path: polygon(0% 0%, 0% -200%, 45% 100%, 0 100%);
    }

    @include grid-media-query(md) {
      height: $bg-img-height-hg-s;
    }
  }

  .image {
    display: block;
    width: 100%;
    height: $bg-img-height-hg-l;
    padding: 0;
    object-fit: cover;

    @include grid-media-query(sm) {
      height: $bg-img-height-hg-s;
      margin-left: auto;
    }

    @include grid-media-query(md) {
      height: $bg-img-height-hg-s;
    }

    @include grid-media-query(lg) {
      width: calc(100% - 380px); // To make the image looks better on large screen
    }
  }

  .infoBox {
    position: relative;
    box-sizing: border-box;
    width: auto;
    padding: $space-100;
    margin: -$space-300 $space-100 0 $space-100;
    background: $color-white;
    border-radius: $border-radius-l;
    box-shadow: $elevation-medium;
    transform: translateY(-50%);

    @include grid-media-query(sm) {
      padding: $space-250 $space-200;
      margin: -$space-300 $space-100 0 $space-100;
    }

    @include grid-media-query(md) {
      max-width: 829px; // TODO: refactor this afterwards
      padding: $space-250 $space-200;
      margin: 0 auto;
    }
  }
}

.title {
  @include heading-1;
  @include vertical-margin(0, $space-100);
}

.description {
  @include body-1;
  @include vertical-margin(0, $space-150);
}

.contactCard {
  padding: $space-100;
  background-color: $color-grey-30;
  border-radius: $border-radius;
}

.contactCardTitle {
  @include heading-4;
  @include vertical-margin(0, $space-50);
}

.contactInfoWrapper {
  display: flex;
  flex-direction: column;
  gap: $space-100;

  @include grid-media-query(sm) {
    flex-direction: row;
  }
}

.contactInfo {
  display: flex;
  flex: 1;
  gap: $space-75;
  align-items: center;

  img {
    flex: 0 0 $avatar-size;
    width: $avatar-size;
    height: $avatar-size;
    border-radius: 50%;
  }

  .contactName {
    @include body-3-bold;
    @include vertical-margin;
  }

  .contactDescription {
    @include body-3;
    @include vertical-margin;
  }
}

.ctaButton {
  align-self: center;
  width: 100%;

  @include grid-media-query(sm) {
    width: auto;
  }
}
</style>
